import React from 'react';
import { cn } from '@/lib/utils';
import { WindowBreakpoints } from '@/hooks/useWindowResponsive';
import { 
  Compass, 
  Briefcase, 
  Monitor, 
  Shield, 
  Wrench,
  MessageSquare,
} from 'lucide-react';

interface AppStoreSidebarProps {
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
  responsive?: WindowBreakpoints & {
    shouldCollapseSidebar: boolean;
    isNarrow: boolean;
    isMedium: boolean;
  };
}

const categories = [
  { id: 'discover',      label: 'Discover',      icon: Compass },
  { id: 'productivity',  label: 'Productivity',  icon: Briefcase },
  { id: 'communication', label: 'Communication', icon: MessageSquare },
  { id: 'utilities',     label: 'Utilities',     icon: Wrench },
  { id: 'system',        label: 'System',        icon: Monitor },
  { id: 'security',      label: 'Security',      icon: Shield },
];

export function AppStoreSidebar({ selectedCategory, onCategorySelect, responsive }: AppStoreSidebarProps) {
  const isCompact = responsive?.isNarrow || responsive?.shouldCollapseSidebar;
  return (
    <div className={cn(
      "h-full backdrop-blur-sm border-r border-zinc-800/60 flex flex-col",
      "bg-gradient-to-b from-zinc-950/90 via-zinc-900/70 to-black/80",
      isCompact ? "w-20" : "w-72"
    )}>
      {/* Refined Header */}
      {!isCompact && (
        <div className="px-6 pt-8 pb-6">
          <div className="relative">
            <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-amber-900/30 to-transparent rounded-full" />
            <h2 className="text-sm font-light text-zinc-100 mb-1 tracking-[0.2em] uppercase">Categories</h2>
            <div className="h-px bg-gradient-to-r from-amber-900/20 via-amber-800/40 to-transparent mt-3" />
          </div>
        </div>
      )}
      
      {/* Refined Vertical Card Layout */}
      <nav className={cn(
        "flex-1 overflow-y-auto",
        isCompact ? "px-2 py-4" : "px-4 pb-6"
      )}>
        <div className={cn(
          "flex flex-col",
          isCompact ? "gap-3" : "gap-3"
        )}>
          {categories.map((category) => {
            const Icon = category.icon;
            const isSelected = selectedCategory === category.id;
            
            return (
              <button
                key={category.id}
                onClick={() => onCategorySelect(category.id)}
                className={cn(
                  "group relative overflow-hidden transition-all duration-300",
                  "backdrop-blur-sm",
                  isCompact ? "rounded-xl p-3" : "rounded-xl p-4",
                  isSelected
                    ? "bg-gradient-to-br from-amber-900/30 via-amber-800/20 to-zinc-900/40"
                    : "bg-gradient-to-br from-zinc-900/40 to-zinc-800/20",
                  isSelected
                    ? "border border-amber-900/50 shadow-lg shadow-amber-900/10"
                    : "border border-zinc-800/40 hover:border-zinc-700/60",
                  !isSelected && "hover:bg-gradient-to-br hover:from-zinc-800/50 hover:to-zinc-900/30"
                )}
                title={isCompact ? category.label : undefined}
              >
                {/* Elegant background accent */}
                <div className={cn(
                  "absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300",
                  isSelected 
                    ? "from-amber-900/10 to-transparent opacity-100" 
                    : "from-zinc-700/10 to-transparent group-hover:opacity-100"
                )} />
                
                {/* Content */}
                <div className={cn(
                  "relative flex items-center",
                  isCompact ? "justify-center" : "gap-4"
                )}>
                  {/* Icon container with refined styling */}
                  <div className={cn(
                    "relative flex-shrink-0 flex items-center justify-center",
                    "transition-all duration-300",
                    isCompact ? "w-full" : ""
                  )}>
                    <div className={cn(
                      "absolute inset-0 rounded-lg transition-all duration-300",
                      isSelected 
                        ? "bg-amber-900/20 blur-sm scale-110" 
                        : "bg-zinc-800/20 group-hover:bg-zinc-700/30 group-hover:scale-105"
                    )} />
                    <Icon className={cn(
                      "relative transition-all duration-300",
                      isCompact ? "h-6 w-6" : "h-6 w-6",
                      isSelected 
                        ? "text-amber-200/90" 
                        : "text-zinc-400 group-hover:text-zinc-300"
                    )} />
                  </div>
                  
                  {/* Label with refined typography */}
                  {!isCompact && (
                    <div className="flex-1 flex items-center justify-between">
                      <span className={cn(
                        "text-sm font-light tracking-wide transition-colors duration-300",
                        isSelected 
                          ? "text-amber-100" 
                          : "text-zinc-400 group-hover:text-zinc-200"
                      )}>
                        {category.label}
                      </span>
                      
                      {/* Refined indicator */}
                      {isSelected && (
                        <div className="flex items-center gap-1.5">
                          <div className="h-1 w-1 rounded-full bg-amber-400/60" />
                          <div className="h-1 w-1 rounded-full bg-amber-400/80" />
                          <div className="h-1 w-1 rounded-full bg-amber-400" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Bottom accent line for selected state */}
                {isSelected && !isCompact && (
                  <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-900/50 to-transparent" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
      
      {/* Elegant footer divider */}
      {!isCompact && (
        <div className="px-6 py-4">
          <div className="h-px bg-gradient-to-r from-transparent via-zinc-800/50 to-transparent" />
        </div>
      )}
    </div>
  );
}
